import * as React from 'react'
import * as Api from 'src/api'
import { useDebouncedLoadOptions } from 'src/helpers/hooks'
import { type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import AsyncSelectInput from 'src/views/components/forms/formik/AsyncSelectInput'
import { type Option } from 'src/views/components/forms/formik/SelectInput'

type AdministratorListsLecturerRow = Api.AdministratorListsLecturer['data'][number]

function listLecturerRowsToOptions(rows: readonly AdministratorListsLecturerRow[]): Option[] {
  return rows.map((row) => {
    const name = row.name.trim().length > 0 ? row.name.trim() : String(row.id)
    const pn = row.persNum?.trim() ?? ''
    const label = pn.length > 0 ? `${name} - ${pn}` : name
    return { value: String(row.id), label }
  })
}

type LecturerSelectFieldProps = {
  readonly headers: AuthenticatedHeaders
  readonly initialKnownLabels?: Readonly<Record<string, string>>
}

export function LecturerSelectField({ headers, initialKnownLabels }: LecturerSelectFieldProps): JSX.Element {
  const t = useTranslatable()

  const loadOptions = useDebouncedLoadOptions(
    React.useCallback(
      async (q: string) => {
        const result = await Api.getAdministratorListsLecturersPersnums({
          headers,
          ...(q.length > 0 ? { query: { q } } : {}),
        })
        return listLecturerRowsToOptions(result.data)
      },
      [headers]
    )
  )

  return (
    <AsyncSelectInput
      name="lecturerId"
      label={t('lecturer:lecturer')}
      storeValueOnly
      cacheOptions
      loadOptions={loadOptions}
      defaultOptions
      placeholder={t('common:select')}
      initialKnownLabels={initialKnownLabels}
    />
  )
}
